import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Confirmation } from '@abp/ng.theme.shared';
import { LocalizationPipe, LocalizationService, PermissionService } from '@abp/ng.core';
import { RouterLink } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { Popover, PopoverModule } from 'primeng/popover';
import { Observable, of } from 'rxjs';
import { filter, finalize, map, switchMap } from 'rxjs/operators';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { AgentListItemDto } from 'src/core/models/agent/agent-list-item.dto';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { AgentService } from 'src/core/services/agent/agent.service';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { StateDefinitionCacheService } from 'src/core/services/state-definition/state-definition-cache.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { SkillDto } from 'src/core/models/skill/skill.dto';
import { PermissionConstants } from 'src/core/constants/permission.constant';
import { AssignedAgentItem } from 'src/app/shared/components/assigned-agents/models/assigned-agent-item.model';
import { AssignedAgentsComponent } from 'src/app/shared/components/assigned-agents/assigned-agents.component';
import { AssignedAgentsFooterDirective } from 'src/app/shared/components/assigned-agents/assigned-agents-footer.directive';
import { SkillManageAgentsDialogComponent } from '../skill-manage-agents-dialog/skill-manage-agents-dialog.component';

/**
 * Assigned agents fetched per popover page. Large enough that the first page overflows the popover's visible
 * area — which is what puts the scrollbar there and makes the scroll-to-load interaction discoverable.
 */
const ASSIGNED_AGENTS_PAGE_SIZE = 20;

type SkillListItem = SkillDto;
type SkillTemplateContext = BaseListColumnTemplateContext<SkillListItem>;

@Component({
  selector: 'app-skill-list',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    RouterLink,
    TooltipModule,
    PopoverModule,
    ActionMenuComponent,
    BaseListComponent,
    AssignedAgentsComponent,
    AssignedAgentsFooterDirective,
    SkillManageAgentsDialogComponent,
  ],
  templateUrl: './skill-list.component.html',
  styleUrls: ['./skill-list.component.scss'],
})
export class SkillListComponent implements OnInit, OnChanges, AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService = inject(CrudService);
  private readonly agentService = inject(AgentService);
  private readonly stateDefinitionCache = inject(StateDefinitionCacheService);
  private readonly permissionService = inject(PermissionService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toasterService = inject(ToasterService);

  @ViewChild('agentsPopover') agentsPopover!: Popover;
  @ViewChild('agentsTpl') agentsTpl!: TemplateRef<SkillTemplateContext>;
  @ViewChild('nameTpl') nameTpl!: TemplateRef<SkillTemplateContext>;
  @ViewChild('statusTpl') statusTpl!: TemplateRef<SkillTemplateContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<SkillTemplateContext>;

  @Input() reloadVersion = 0;

  @Output() addNew = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();

  readonly agentManagementLink = '/administration/agent-management';

  skills: SkillListItem[] = [];
  loading = false;
  listConfig!: BaseListConfig<SkillListItem>;
  selectedSkillForManage: SkillListItem | null = null;
  showManageAgentsDialog = false;

  agentsPopoverSkillId: number | null = null;
  agentsPopoverOpen = false;
  agentsPopoverQueryParams: Record<string, string> = {};
  popoverAgents: AssignedAgentItem[] = [];
  popoverLoading = false;
  popoverLoadingMore = false;
  popoverTotalCount: number | null = null;

  private assignedAgentsRequestId = 0;

  get activeCount(): number {
    return this.skills.filter(s => s.isActive).length;
  }

  get canViewAgentManagement(): boolean {
    return this.permissionService.getGrantedPolicy(PermissionConstants.agentManagement);
  }

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadVersion'] && !changes['reloadVersion'].firstChange) {
      this.loadList();
    }
  }

  ngAfterViewInit(): void {
    const l = (key: string) => this.localization.instant(key);

    const columns: BaseListColumn<SkillListItem>[] = [
      {
        field: 'name',
        header: l('Settings::WorkEnvironment:Skills:SkillName'),
        bodyTemplate: this.nameTpl,
      },
      {
        field: 'agentCount',
        header: l('Settings::WorkEnvironment:Skills:Agents'),
        width: '140px',
        bodyTemplate: this.agentsTpl,
      },
      {
        field: 'isActive',
        header: l('Settings::WorkEnvironment:Skills:Status'),
        width: '140px',
        bodyTemplate: this.statusTpl,
      },
      {
        field: 'id',
        header: '',
        width: '60px',
        bodyTemplate: this.actionsTpl,
      },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('Settings::WorkEnvironment:Skills:EmptyMessage'),
    };

    this.cdr.detectChanges();
  }

  getMenuItems(row: SkillListItem): ActionMenuItem[] {
    const hasAgents = row.agentCount > 0;
    return [
      {
        label: this.localization.instant('Settings::WorkEnvironment:Skills:ManageAgents'),
        icon: 'pi pi-users',
        action: () => this.onManageAgents(row),
      },
      {
        label: this.localization.instant('Settings::WorkEnvironment:Skills:Delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        disabled: hasAgents,
        tooltip: hasAgents
          ? this.localization.instant('Settings::WorkEnvironment:Skills:DeleteDisabledTooltip')
          : undefined,
        action: () => this.onDeleteClick(row),
      },
    ];
  }

  onAgentsClick(row: SkillListItem, event: Event): void {
    event.stopPropagation();

    const isClosing = this.agentsPopoverOpen && this.agentsPopoverSkillId === row.id;

    this.agentsPopoverSkillId = row.id;
    this.agentsPopoverQueryParams = { skillId: String(row.id) };
    this.agentsPopover.toggle(event);

    if (isClosing) {
      return;
    }

    this.popoverAgents = [];
    this.popoverTotalCount = null;
    this.popoverLoading = true;

    this.fetchAssignedAgents(row.id, 0).subscribe({
      next: response => {
        this.popoverAgents = this.toAssignedAgentItems(response.items ?? []);
        this.popoverTotalCount = response.totalCount ?? 0;
      },
      error: () => {
        this.popoverAgents = [];
        this.popoverTotalCount = 0;
      },
    });
  }

  onAgentsLoadMore(): void {
    const skillId = this.agentsPopoverSkillId;
    if (skillId === null || this.popoverLoading || this.popoverLoadingMore) {
      return;
    }

    this.popoverLoadingMore = true;

    this.fetchAssignedAgents(skillId, this.popoverAgents.length).subscribe({
      next: response => {
        this.popoverAgents = [
          ...this.popoverAgents,
          ...this.toAssignedAgentItems(response.items ?? []),
        ];
        this.popoverTotalCount = response.totalCount ?? this.popoverAgents.length;
      },
    });
  }

  onAgentsPopoverShow(): void {
    this.agentsPopoverOpen = true;
  }

  onAgentsPopoverHide(): void {
    this.agentsPopoverOpen = false;
  }

  onManageAgents(row: SkillListItem): void {
    this.selectedSkillForManage = row;
    this.showManageAgentsDialog = true;
  }

  onManageAgentsSaved(): void {
    this.loadList();
  }

  onDeleteClick(row: SkillListItem): void {
    this.confirmationService
      .warn(
        this.localization.instant(
          'Settings::WorkEnvironment:Skills:DeleteConfirmMessage',
          row.name,
        ),
        'Settings::WorkEnvironment:Skills:DeleteConfirmTitle',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'Settings::WorkEnvironment:Skills:Delete',
          cancelText: 'AbpUi::Cancel',
        },
      )
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.crudService.delete(SkillDto, row.id).pipe(map(() => true));
          }

          return of(false);
        }),
      )
      .subscribe({
        next: isDeleted => {
          if (!isDeleted) {
            return;
          }

          this.toasterService.success('::SuccessfullyDeleted');
          this.deleted.emit();
        },
      });
  }

  private fetchAssignedAgents(
    skillId: number,
    skipCount: number,
  ): Observable<ListResponseDto<AgentListItemDto>> {
    const requestId = ++this.assignedAgentsRequestId;

    const request: ListRequestDto = {
      skipCount,
      maxResultCount: ASSIGNED_AGENTS_PAGE_SIZE,
      filters: [{ field: 'skillIds', operator: 0, value: [skillId] }],
      sorters: [{ field: '*', direction: 'asc' }],
    };

    return this.agentService.getAgents(request).pipe(
      filter(() => requestId === this.assignedAgentsRequestId),
      finalize(() => {
        if (requestId !== this.assignedAgentsRequestId) {
          return;
        }

        this.popoverLoading = false;
        this.popoverLoadingMore = false;
      }),
    );
  }

  private toAssignedAgentItems(agents: AgentListItemDto[]): AssignedAgentItem[] {
    return agents.map(agent => {
      const state =
        agent.stateId == null ? undefined : this.stateDefinitionCache.getByIdSync(agent.stateId);

      return {
        userId: agent.id,
        fullName: agent.name?.trim() || agent.userName,
        stateName: state?.name ?? null,
        stateType: state?.typeId ?? null,
      };
    });
  }

  private loadList(): void {
    this.loading = true;

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [],
      sorters: [
        {
          field: 'creationTime',
          direction: 'desc',
        },
      ],
    };

    this.crudService.get<SkillDto>(SkillDto, request).subscribe({
      next: (response: ListResponseDto<SkillDto>) => {
        this.skills = response.items ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }
}
